import { useTranslation } from "next-i18next";
import { useRouter } from "next/router";
import React from "react";

import { RouteName } from "@/consts";
import { AnalyticsEventEnum } from "@/consts/AnalyticsEventEnum";
import { scrollToSection } from "@/features/post/helper";

import { NFTAccount } from "./NFTAccount";
import { StatisticBlock } from "./StatisticBlock";

interface AboutProps {
  isOwner?: boolean;
  walletAccount?: string;
  channelDescription?: string;
  aboutInformation: {
    numberOfPosts: number;
    followers: number;
    monthlyPosts?: number;
    subscriberCount: number;
  };
  blurPostCount: {
    currentMonth: number;
    lastMonth: number;
    total: number;
  };
}

export const About: React.FC<AboutProps> = (props) => {
  const router = useRouter();
  const { t } = useTranslation("profilePublic");
  const { isOwner = false, walletAccount, channelDescription } = props;
  const { numberOfPosts, monthlyPosts, followers, subscriberCount } =
    props.aboutInformation;
  const { currentMonth, lastMonth, total } = props.blurPostCount;

  const isUserProfilePage = router.route === RouteName.ProfileUserRoute;

  const onClickPostStat = () =>
    scrollToSection({
      anchor: "#post-section",
      analyticsEnum: AnalyticsEventEnum.PublicProfileScrollToPostSection,
      offset: 64, //matching with h-16, height of top bar
    });

  const onClickStatiscBlock = (routeName: RouteName) => {
    if (!isOwner) return null;
    return () => router.push(routeName);
  };

  return (
    <div
      className="relative flex flex-col w-full border border-solid border-gray-200 rounded-lg even-bg-gray-50"
      data-test-id="About-container"
    >
      {channelDescription && (
        <div className="flex flex-col p-3 space-y-2">
          <span className="text-lg  text-gray-900">
            {t("profilePublic:channelDescription")}
          </span>
          <div className="text-base  text-gray-700 truncate whitespace-pre-wrap">
            {channelDescription}
          </div>
        </div>
      )}

      {/* Title */}
      <div className="flex flex-col p-3 space-y-2">
        <span className="text-lg  text-gray-900">{t("about")}</span>

        <div className="grid grid-cols-3 divide-x divide-gray-100">
          <StatisticBlock
            onClick={onClickPostStat}
            label={t("aboutSection.posts")}
            value={numberOfPosts}
            quantifier={t("aboutSection.piece")}
          />

          {isUserProfilePage ? (
            <StatisticBlock
              label={t("aboutSection.monthlyPosts")}
              value={monthlyPosts ?? 0}
              quantifier={t("aboutSection.piece")}
            />
          ) : (
            <StatisticBlock
              onClick={onClickStatiscBlock(RouteName.MyAudienceUser)}
              label={t("aboutSection.followers")}
              value={followers}
            />
          )}

          <StatisticBlock
            onClick={onClickStatiscBlock(
              RouteName.SubscriptionPlansSubscribers,
            )}
            label={t("aboutSection.subscriberCount")}
            value={subscriberCount}
          />
        </div>
      </div>

      {/* remove subscripton content count */}
      <div className="flex flex-col p-3 space-y-2">
        <span className="text-lg  text-gray-900">
          {t("aboutSection.subscribedContent")}
        </span>

        <div className="grid grid-cols-3 divide-x divide-white">
          <StatisticBlock
            onClick={onClickStatiscBlock(RouteName.MySubscription)}
            label={t("aboutSection.currentMonth")}
            value={currentMonth}
            quantifier={t("aboutSection.piece")}
          />

          <StatisticBlock
            onClick={onClickStatiscBlock(RouteName.MySubscription)}
            label={t("aboutSection.lastMonth")}
            value={lastMonth}
            quantifier={t("aboutSection.piece")}
          />

          <StatisticBlock
            label={t("aboutSection.total")}
            value={total}
            quantifier={t("aboutSection.piece")}
            onClick={onClickStatiscBlock(RouteName.MySubscription)}
          />
        </div>
      </div>

      {/* {walletAccount && <NFTAccount walletAccount={walletAccount} />} */}
    </div>
  );
};
